import CloseOutlined from '@ant-design/icons/CloseOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Form from 'antd/es/form';
import Steps from 'antd/es/steps';
import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { useTheme } from 'styled-components';

import { useCreateAgentConfigurationVersion } from '../../../queries/agent-configuration-versions';
import { useCreateAgent } from '../../../queries/agents';
import { AgentBasicsForm } from '../../CreateAgentForm/AgentBasicsForm/AgentBasicsForm';
import { AgentInstructionsForm } from '../../CreateAgentForm/AgentInstructionsForm/AgentInstructionsForm.component';
import { AgentReviewForm } from '../../CreateAgentForm/AgentsReviewForm/AgentReviewForm.component';
import { AgentTypeSelector } from '../../CreateAgentForm/AgentTypeSelectorForm/AgentTypeSelector.component';
import { AGENT_TYPE_PRESETS } from '../../CreateAgentForm/presets';

import { 
  type AgentFormData,
  defaultFormData,
  getFieldsForStep,
  isNextDisabled,
  getNextButtonText,
  createConfigurationData,
} from './CreateAgentModal.utils';
import { 
  StyledModal, 
  StepsContainer, 
  ContentContainer, 
  FooterContainer 
} from './CreateAgentModal.styles';

const { Step } = Steps;

interface CreateAgentModalProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
}

export const CreateAgentModal: React.FC<CreateAgentModalProps> = ({
  open,
  onClose,
  teamId,
}) => {
  const { message } = App.useApp();
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm<AgentFormData>();
  const [formData, setFormData] = useState<AgentFormData>(defaultFormData);
  const theme = useTheme();

  const createAgentMutation = useCreateAgent();
  const createConfigMutation = useCreateAgentConfigurationVersion();

  const handleNext = async () => {
    try {
      // Validate current step
      const fields = getFieldsForStep(currentStep);
      await form.validateFields(fields);

      // Update form data
      const values = form.getFieldsValue();
      setFormData({ ...formData, ...values });

      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        // Final step - create agent
        await handleCreate();
      }
    } catch (error) {
      // Validation error - form will show error messages
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      // TODO: Save current form data before going back - ie. if you go back from instructions to type, the instructions will be saved
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreate = async () => {
    try {
      const values = form.getFieldsValue(true);

      // Step 1: Create the agent
      const agent = await createAgentMutation.mutateAsync({
        teamId,
        name: values.name,
        description: values.description || null,
      });

      // Step 2: Create initial configuration version
      const configData = createConfigurationData(agent.agentId, values);
      await createConfigMutation.mutateAsync(configData);

      void message.success(`Agent "${values.name}" created successfully!`);
      handleClose();
    } catch (error) {
      const errorMessage =
        error instanceof AxiosError
          ? error.response?.data?.error?.message || 'Failed to create agent'
          : 'Failed to create agent';
      void message.error(errorMessage);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setFormData(defaultFormData);
    setCurrentStep(0);
    onClose();
  };

  const isPending = createAgentMutation.isPending || createConfigMutation.isPending;

  return (
    <StyledModal
      open={open}
      onCancel={handleClose}
      width={720}
      footer={null}
      closeIcon={<CloseOutlined />}
      title="Create New Agent"
    >
      <StepsContainer>
        <Steps current={currentStep} size="small">
          <Step title="Basics" />
          <Step title="Type" />
          <Step title="Instructions" />
          <Step title="Review" />
        </Steps>
      </StepsContainer>

      <Form
        form={form}
        layout="vertical"
        initialValues={defaultFormData}
        onValuesChange={(_, allValues) => {
          setFormData({ ...formData, ...allValues });
        }}
      >
        <ContentContainer>
          {currentStep === 0 && <AgentBasicsForm form={form} />}
          {currentStep === 1 && (
            <AgentTypeSelector
              form={form}
              onTypeSelect={(type) => {
                const preset =
                  type !== 'custom' ? AGENT_TYPE_PRESETS[type] : null;
                if (preset) {
                  form.setFieldsValue({
                    instructions: preset.instructions,
                    tools: preset.tools,
                  });
                }
              }}
            />
          )}
          {currentStep === 2 && (
            <AgentInstructionsForm form={form} agentType={formData.agentType} />
          )}
          {currentStep === 3 && <AgentReviewForm formData={formData} />}
        </ContentContainer>

        <FooterContainer>
          <Button onClick={handleClose}>Cancel</Button>
          <div>
            {currentStep > 0 && (
              <Button
                onClick={handlePrevious}
                style={{ marginRight: `${theme.marginXS}px` }}
              >
                Previous
              </Button>
            )}
            <Button
              type="primary"
              onClick={handleNext}
              disabled={isNextDisabled(currentStep, formData.agentType)}
              loading={isPending}
            >
              {getNextButtonText(currentStep, isPending)}
            </Button>
          </div>
        </FooterContainer>
      </Form>
    </StyledModal>
  );
}; 